import { useCallback, useRef, useState } from 'react';
import { receivablesService } from '../services/receivablesService';
import { useTasksStore } from '../stores/tasksStore';
import { useIsAcomba } from './useIsAcomba';
import type { ReceivablesSummaryDto } from '../types/kpi/receivablesSummary';

interface ReceivablesSummaryState {
    summary: ReceivablesSummaryDto | null;
    /** Nothing has answered yet in this visit. */
    showPlaceholder: boolean;
    reload: () => Promise<void>;
    clear: () => void;
}

/**
 * The portfolio figures behind the receivables block (home.receivables.*). Read on every arrival and
 * never seeded from a browser-persisted copy: a figure from an earlier session could be false.
 */
export function useReceivablesSummary(): ReceivablesSummaryState {
    const [summary, setSummary] = useState<ReceivablesSummaryDto | null>(null);
    const [answered, setAnswered] = useState(false);
    const isAcomba = useIsAcomba();

    // Distinguishes the answer being waited for from one already superseded: Home refreshes from several
    // triggers at once (visibility, panel restore, sync completion), and without this the slower of two
    // overlapping reads would win and pin an older figure.
    const latestRead = useRef(0);

    const reload = useCallback(async () => {
        const read = ++latestRead.current;

        // The block is web-only: the desktop source cannot serve it.
        if (isAcomba) {
            setSummary(null);
            setAnswered(true);
            return;
        }

        // Read through the store rather than from scope: this reload is listed in the page's shared
        // refresh, and depending on the flag here would re-create it and re-run that refresh.
        if (useTasksStore.getState().dailyTasks?.relanceAccessible === false) {
            setSummary(null);
            setAnswered(true);
            return;
        }

        try {
            const result = await receivablesService.getSummary();
            if (read !== latestRead.current) return;
            setSummary(result);
        } catch {
            // Advisory: a failure hides the block and leaves the reminder list alone.
            if (read !== latestRead.current) return;
            setSummary(null);
        } finally {
            if (read === latestRead.current) setAnswered(true);
        }
    }, [isAcomba]);

    const clear = useCallback(() => {
        // Supersedes any read in flight: its answer belongs to the company being left.
        latestRead.current++;
        setSummary(null);
        setAnswered(false);
    }, []);

    return { summary, showPlaceholder: !answered, reload, clear };
}
